import { useState } from 'react';
import { Plus, X, CircleAlert as AlertCircle } from 'lucide-react';

const MAX_CONTACTS = 2;

export function ExtraContactsCard({
  icon,
  itemIcon,
  title,
  description,
  placeholder,
  inputType,
  values,
  saving,
  normalize,
  display,
  invalidMessage,
  onSave,
}: {
  icon:           React.ReactNode;
  itemIcon:       React.ReactNode;
  title:          string;
  description:    string;
  placeholder:    string;
  inputType:      'email' | 'tel';
  values:         string[];
  saving:         boolean;
  normalize:      (raw: string) => string | null;
  display?:       (value: string) => string;
  invalidMessage: string;
  onSave:         (next: string[], action: 'add' | 'remove') => void;
}) {
  const [input, setInput] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleAdd = () => {
    setError(null);
    if (!input.trim()) return;
    const value = normalize(input);
    if (!value) { setError(invalidMessage); return; }
    if (values.includes(value)) { setError('כבר קיים ברשימה'); return; }
    if (values.length >= MAX_CONTACTS) { setError(`ניתן להוסיף עד ${MAX_CONTACTS}`); return; }
    onSave([...values, value], 'add');
    setInput('');
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
      <div className="flex items-center gap-2">
        {icon}
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      </div>
      <p className="text-xs text-gray-500 leading-relaxed">{description}</p>

      {values.length > 0 && (
        <div className="space-y-2">
          {values.map((v) => (
            <div
              key={v}
              className="flex items-center justify-between gap-3 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2"
            >
              <div className="flex items-center gap-2 min-w-0">
                {itemIcon}
                <span className="text-sm text-gray-700 truncate" dir="ltr">{display ? display(v) : v}</span>
              </div>
              <button
                type="button"
                onClick={() => onSave(values.filter((x) => x !== v), 'remove')}
                disabled={saving}
                className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors disabled:opacity-40"
                title="הסר"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {values.length < MAX_CONTACTS ? (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <input
              type={inputType}
              dir="ltr"
              value={input}
              onChange={(e) => { setInput(e.target.value); setError(null); }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
              placeholder={placeholder}
              className="flex-1 text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder:text-gray-400"
            />
            <button
              type="button"
              onClick={handleAdd}
              disabled={saving || !input.trim()}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Plus className="w-4 h-4" />
              הוסף
            </button>
          </div>
          {error && (
            <p className="text-xs text-red-600 flex items-center gap-1">
              <AlertCircle className="w-3.5 h-3.5" />
              {error}
            </p>
          )}
        </div>
      ) : (
        <p className="text-xs text-amber-600">הגעת למקסימום של {MAX_CONTACTS}.</p>
      )}
    </div>
  );
}
